import { useIntl } from 'react-intl'
import Pagination from './Pagination'
import { currency } from '../helpers/currency'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import clsx from 'clsx'

export interface ColumnType {
  title: string
  tooltip?: string
  dataIndex: string
  key: string
  render?: (text: string, data: ColumnType, index: number) => JSX.Element
  status?: boolean
  yesOrNo?: boolean
  send?: boolean
  storeStatus?: boolean
  dropdown?: any[]
  monetize?: boolean
  customMonetize?: boolean
  mailSent?: boolean
  sorter?: boolean
  sortKey?: string
}

interface TableProps {
  loading?: boolean
  tableName?: string
  selectedRowKeys?: string[]
  setSelectedRowKeys?: (keys: any[]) => void
  columns: ColumnType[]
  dataSource: any
  orderList?: any[]
  defaultOrderList?: any[]
  onSort?: (orderList: any[]) => void
  pagination?: {
    current: number
    pageSize: number
    total: number
    onChange: (pagination: { current: number; pageSize: number; total: number }) => void
  }
}

export function Table({
  loading,
  tableName,
  selectedRowKeys,
  setSelectedRowKeys,
  columns,
  dataSource,
  orderList,
  defaultOrderList = [],
  onSort,
  pagination,
}: TableProps) {
  const intl = useIntl()

  const handleSort = (column: ColumnType) => {
    if (!onSort || !column.sorter || !column.sortKey) return

    const currentSort = orderList?.find((item) => item.key === column.sortKey)
    let newOrderList: any[] = []

    if (!currentSort) {
      newOrderList = [{ type: 1, key: column.sortKey }]
    } else if (currentSort.type === 1) {
      newOrderList = [{ type: 2, key: column.sortKey }]
    } else {
      newOrderList = defaultOrderList
    }

    onSort(newOrderList)
  }

  const getSortIcon = (column: ColumnType) => {
    if (!column.sorter || !column.sortKey) return null
    const currentSort = orderList?.find((item) => item.key === column.sortKey)

    if (!currentSort) {
      return <i className='ki-outline ki-sort fs-7 ms-1 text-gray-400'></i>
    }

    return currentSort.type === 1 ? (
      <i className='ki-outline ki-arrow-up fs-7 ms-1 text-primary'></i>
    ) : (
      <i className='ki-outline ki-arrow-down fs-7 ms-1 text-primary'></i>
    )
  }

  const sendCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'SEND' })
    const passiveText = intl.formatMessage({ id: 'NOT_SEND' })

    let text = passiveText
    let boolen = true

    switch (input) {
      case '1':
        text = activeTex
        boolen = true
        break
      case '0':
        text = passiveText
        boolen = false
        break
      case 2:
        text = activeTex
        boolen = true
        break
      case 1:
        text = activeTex
        boolen = true
        break
      case 0:
        text = passiveText
        boolen = false
        break
    }

    return { boolen, text }
  }

  const mailSentCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'SEND' })
    const pendingText = intl.formatMessage({ id: 'PENDING' })
    const scheduledText = intl.formatMessage({ id: 'SCHEDULED' })
    const passiveText = intl.formatMessage({ id: 'NOT_SEND' })

    let text = passiveText
    let boolen = true
    let color = 'danger'

    switch (input) {
      case 1:
        text = pendingText
        boolen = true
        color = 'warning'
        break
      case 2:
        text = activeTex
        boolen = false
        color = 'success'
        break
      case 3:
        text = passiveText
        boolen = false
        color = 'danger'
        break
      case 4:
        text = scheduledText
        boolen = true
        color = 'info'
        break
      case 0:
        text = passiveText
        boolen = false
        color = 'danger'
        break
    }

    return { boolen, text, color }
  }

  const statusCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'ACTIVE' })
    const passiveText = intl.formatMessage({ id: 'PASSIVE' })
    const inactiveText = intl.formatMessage({ id: 'INACTIVE' })
    const loyaltyEnabled = intl.formatMessage({ id: 'LOYALTY_ENABLED' })
    const loyaltyDisabled = intl.formatMessage({ id: 'LOYALTY_DISABLED' })

    let text = passiveText
    let boolen = true

    switch (input) {
      case true:
        text = activeTex
        boolen = true
        break
      case false:
        text = passiveText
        boolen = false
        break
      case activeTex:
        text = activeTex
        boolen = true
        break
      case passiveText:
        text = passiveText
        boolen = false
        break
      case '1':
        text = activeTex
        boolen = true
        break
      case '0':
        text = passiveText
        boolen = false
        break
      case '2':
        text = inactiveText
        boolen = false
        break
      case '3':
        text = passiveText
        boolen = false
        break
      case 1:
        text = activeTex
        boolen = true
        break
      case 2:
        text = inactiveText
        boolen = false
        break
      case 3:
        text = passiveText
        boolen = false
        break
      case 0:
        text = passiveText
        boolen = false
        break
      case 'Loyalty Enabled':
        text = loyaltyEnabled
        boolen = true
        break
      case 'Loyalty Disabled':
        text = loyaltyDisabled
        boolen = false
        break
    }

    return { boolen, text }
  }

  const yesOrNoCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'YES' })
    const passiveText = intl.formatMessage({ id: 'NO' })

    let text = passiveText
    let boolen = true

    switch (input) {
      case true:
        text = activeTex
        boolen = true
        break
      case false:
        text = passiveText
        boolen = false
        break
    }

    return { boolen, text }
  }

  const storeStatusCheck = (input: any) => {
    const activeTex = intl.formatMessage({ id: 'Active' })
    const passiveText = intl.formatMessage({ id: 'Passive' })
    const inactiveText = intl.formatMessage({ id: 'Inactive' })

    let text = passiveText
    let boolen = true

    switch (input) {
      case 1:
        text = activeTex
        boolen = true
        break
      case 2:
        text = inactiveText
        boolen = false
        break
      case 3:
        text = passiveText
        boolen = false
        break
    }

    return { boolen, text }
  }

  return (
    <div style={{ position: 'relative', overflowY: 'auto', width: '100%' }}>
      <table
        className='table align-middle table-row-dashed table-striped fs-6 gy-5'
        id='kt_customers_table'
        data-tname={tableName}
      >
        <thead>
          <tr className='text-start fw-bold fs-7 gs-0'>
            {setSelectedRowKeys && (
              <th className='w-10px pe-2'>
                <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    data-kt-check='true'
                    data-kt-check-target='#kt_customers_table .form-check-input'
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRowKeys(dataSource.map((row) => row.id))
                      } else {
                        setSelectedRowKeys([])
                      }
                    }}
                  />
                </div>
              </th>
            )}
            {columns.map((column, indexColumn) => {
              return (
                <th
                  className={clsx(
                    column.key === 'EarnedPoints' || column.key === 'RedemeedPoints'
                      ? 'bg-danger border'
                      : ''
                  )}
                  key={indexColumn}
                  data-key={column.dataIndex}
                  style={{
                    width: column.monetize || column.customMonetize ? '200px' : 'auto',
                    textAlign: column.monetize || column.customMonetize ? 'right' : 'left',
                    paddingRight: column.monetize || column.customMonetize ? '100px' : '0',
                    cursor: column.sorter ? 'pointer' : 'default',
                  }}
                  onClick={() => handleSort(column)}
                >
                  {column.title}
                  {getSortIcon(column)}
                  {column.tooltip && (
                    <span className='position-relative z-50'>
                      <OverlayTrigger placement='top' overlay={<Tooltip>{column.tooltip}</Tooltip>}>
                        <i className='bi bi-info-circle text-primary ms-1'></i>
                      </OverlayTrigger>
                    </span>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        {!loading && (
          <tbody className='fw-semibold text-gray-600'>
            {dataSource?.slice(0, pagination?.pageSize || 10).map((row, rowIndex) => (
              <tr key={rowIndex} className='text-start'>
                {selectedRowKeys && setSelectedRowKeys && (
                  <td className=''>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRowKeys([...selectedRowKeys, row.id])
                          } else {
                            setSelectedRowKeys(selectedRowKeys.filter((key) => key !== row.id))
                          }
                        }}
                      />
                    </div>
                  </td>
                )}
                {columns.map((column, indexColumn) => {
                  return column.render ? (
                    <td key={indexColumn}>{column.render(row[column.dataIndex], row, rowIndex)}</td>
                  ) : column.status ? (
                    <td key={indexColumn}>
                      <span
                        className={`badge badge-light-${statusCheck(row[column.dataIndex]).boolen ? 'success' : 'danger'
                          } fw-bolder`}
                      >
                        {statusCheck(row[column.dataIndex]).text}
                      </span>
                    </td>
                  ) : column.storeStatus ? (
                    <td key={indexColumn}>
                      <span
                        className={`badge badge-light-${storeStatusCheck(row[column.dataIndex]).boolen ? 'success' : 'danger'
                          } fw-bolder`}
                      >
                        {storeStatusCheck(row[column.dataIndex]).text}
                      </span>
                    </td>
                  ) : column.yesOrNo ? (
                    <td key={indexColumn}>
                      <span
                        className={`badge badge-light-${yesOrNoCheck(row[column.dataIndex]).boolen ? 'success' : 'danger'
                          } fw-bolder`}
                      >
                        {yesOrNoCheck(row[column.dataIndex]).text}
                      </span>
                    </td>
                  ) : column.send ? (
                    <td key={indexColumn}>
                      <span
                        className={`badge badge-light-${sendCheck(row[column.dataIndex]).boolen ? 'success' : 'danger'
                          } fw-bolder`}
                      >
                        {sendCheck(row[column.dataIndex]).text}
                      </span>
                    </td>
                  ) : column.mailSent ? (
                    <td key={indexColumn}>
                      <span
                        className={`badge badge-light-${mailSentCheck(row[column.dataIndex]).color
                          }`}
                      >
                        {mailSentCheck(row[column.dataIndex]).text}
                      </span>
                    </td>
                  ) : column.monetize ? (
                    <td key={indexColumn} style={{ textAlign: 'right', paddingRight: '100px' }}>
                      {currency(row[column.dataIndex])}
                    </td>
                  ) : column.customMonetize ? (
                    <td key={indexColumn} style={{ textAlign: 'right', paddingRight: '100px' }}>
                      {row.type === 'Refund'
                        ? currency((row?.totalAmount - row?.discountAmount) * -1 || 0)
                        : row.type === 'Birthday' ||
                          row.type === 'SignUp' ||
                          row.type.includes('Referral')
                          ? '-'
                          : currency(row[column.dataIndex] || 0)}
                    </td>
                  ) : (
                    <td key={indexColumn}>{row[column.dataIndex]}</td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        )}
      </table>

      {!loading && dataSource?.length === 0 && (
        <div
          style={{
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {intl.formatMessage({ id: 'NOT_FOUND' })}
        </div>
      )}

      {loading && (
        <div
          style={{
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {intl.formatMessage({ id: 'LOADING' })}...
        </div>
      )}

      {pagination && (
        <div className='d-flex justify-content-between align-items-center py-10'>
          <div className='d-flex align-items-center'>
            <select
              style={{ width: 100 }}
              className='form-select'
              value={pagination.pageSize}
              onChange={(e) => {
                pagination.onChange({
                  ...pagination,
                  pageSize: parseInt(e.target.value),
                })
              }}
            >
              <option value='5'>5</option>
              <option value='10'>10</option>
              <option value='20'>20</option>
              <option value='30'>30</option>
              <option value='50'>50</option>
            </select>
            <div style={{ marginLeft: 10, whiteSpace: 'nowrap' }}>
              {intl.formatMessage({ id: 'SHOWING' })}{' '}
              {(pagination.current - 1) * pagination.pageSize == 0
                ? 1
                : (pagination.current - 1) * pagination.pageSize}{' '}
              {intl.formatMessage({ id: 'TO' })}{' '}
              {pagination.current * pagination.pageSize > pagination.total
                ? pagination.total
                : pagination.current * pagination.pageSize}{' '}
              {intl.formatMessage({ id: 'OF' })} {pagination.total}{' '}
              {intl.formatMessage({ id: 'RECORDS' })}
            </div>
          </div>
          <Pagination
            currentPage={pagination.current || 1}
            totalPages={Math.ceil(pagination.total / pagination.pageSize) || 1}
            setCurrentPage={(page) => {
              pagination.onChange({ ...pagination, current: page })
            }}
          />
        </div>
      )}
    </div>
  )
}